import React, { useEffect, useState } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import ItemImage from '@components/cart/ProductImage';
import Colors from '@components/shared/Colors';
import { CartItem, StickyBannerData } from '@contracts';
import useNavigate from '@utils/useNavigate';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import Button from './Button';

interface Props {
  data: StickyBannerData;
  cartItem: CartItem;
  scrollToRef?: any;
  scrollHeight?: number;
  showSubtitle?: boolean;
  hideIconMobile?: boolean;
  buttonHandler?: () => void;
}

const StickyBanner: React.FC<Props> = ({
  data,
  cartItem,
  scrollToRef,
  scrollHeight,
  showSubtitle = false,
  hideIconMobile = false,
  buttonHandler
}) => {
  const { navigateWrapper } = useNavigate();
  const [isHidden, setIsHidden] = useState(true);
  const buttonText = data.buttonText ? data.buttonText : 'Buy now';
  const productIcon = getImage(data.icon);
  const { buttonLink } = data;
  const titleRef = useReplacedInnerHtml(data.title);

  const showBanner = () => {
    if (window.scrollY > (scrollHeight || window.innerHeight)) {
      if (document) document.body.classList.add('has-stickybanner');
      setIsHidden(false);
    } else {
      if (document) document.body.classList.remove('has-stickybanner');
      setIsHidden(true);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', showBanner, { passive: true });

    return () => {
      window.removeEventListener('scroll', showBanner);
      document.body.classList.remove('has-stickybanner');
    };
  }, []);

  const scroll = () => {
    scrollToRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleButtonClick = () => {
    if (buttonHandler) {
      buttonHandler();
    } else if (buttonLink) {
      navigateWrapper(buttonLink);
    } else {
      scroll();
    }
  };

  return (
    <Container $isHidden={isHidden} $textColor={data.textColor}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconWrap $hideIconMobile={hideIconMobile}>
          {productIcon ? (
            <ImgWrap>
              <ImgStyled image={productIcon} alt={productIcon?.alt || data?.productCode || ''} />
            </ImgWrap>
          ) : (
            <ItemImage id={data.productCode} />
          )}
        </IconWrap>
        <TextWrap>
          <Title ref={titleRef} />
          {showSubtitle && <Subtitle dangerouslySetInnerHTML={{ __html: data.subtitle }} />}
        </TextWrap>
      </div>
      {/* LEFT FOR V2
      <AddToCart cartItem={cartItem} /> */}
      <StyledButton onClick={() => handleButtonClick()}>{buttonText}</StyledButton>
    </Container>
  );
};

export default StickyBanner;

interface StyleProps {
  $isHidden?: boolean;
  $textColor?: string;
  $hideIconMobile?: boolean;
}

const Container = styled.div<StyleProps>`
  z-index: 999;
  border: 1px solid ${Colors.ecru};
  height: 80px;
  display: flex;
  position: fixed;
  bottom: ${(props) => (props.$isHidden ? '-80px' : '0')};
  transition: bottom 0.3s ease-in-out;
  background: ${Colors.cream};
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding-inline: 120px;
  color: ${(props) => props.$textColor};

  @media (max-width: 1024px) {
    padding-inline: 20px;
  }
`;

const TextWrap = styled.div``;

const Title = styled.h4`
  font-family: BrownStd;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  text-align: left;

  @media (max-width: 1024px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Subtitle = styled.h5`
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  opacity: 50%;
`;

const IconWrap = styled.div<StyleProps>`
  @media (max-width: 1024px) {
    display: ${(props) => (props.$hideIconMobile ? 'none' : 'flex')};
  }
`;

const StyledButton = styled(Button)`
  margin-left: 15px;
`;

const ImgWrap = styled.div`
  width: 21px;
  height: 29px;
  overflow: hidden;
  margin-right: 25px;
`;

const ImgStyled = styled(GatsbyImage)`
  display: block;
  width: 100%;
  height: 100%;
`;
